import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Bell, Check, X, ExternalLink, Search } from "lucide-react";

const TYPE_CONFIG = {
  rfq_new:        { color: "var(--amber-400)", label: "New RFQ"          },
  quote_new:      { color: "var(--amber-500)", label: "Quote received"   },
  rfq_awarded:    { color: "var(--emerald)",   label: "RFQ awarded"      },
  rfq_expired:    { color: "var(--ink-mute)",  label: "RFQ expired"      },
  ticket_new:     { color: "var(--amber-400)", label: "New ticket"       },
  ticket_reply:   { color: "var(--amber-400)", label: "Ticket reply"     },
  ticket_resolved:{ color: "var(--emerald)",   label: "Ticket resolved"  },
  invite_sent:    { color: "var(--amber-400)", label: "Team invite"      },
  // Order lifecycle
  award:                    { color: "var(--emerald)",   label: "Order awarded"     },
  order_update:             { color: "var(--amber-400)", label: "Order update"      },
  order_tax_set:            { color: "var(--amber-500)", label: "Tax breakdown"     },
  order_invoice_submitted:  { color: "var(--amber-500)", label: "Invoice received"  },
  order_buyer_marked_paid:  { color: "#3b82f6",          label: "Payment sent"      },
  order_settled:            { color: "var(--emerald)",   label: "Payment confirmed" },
  order_disputed:           { color: "var(--red)",       label: "Order disputed"    },
  order_milestone:          { color: "var(--amber-400)", label: "Order milestone"   },
  order_delivered:          { color: "var(--emerald)",   label: "Delivery"          },
  verification_approved: { color: "var(--emerald)", label: "Verified"    },
  verification_rejected: { color: "var(--red)", label: "Rejected"        },
  verification:             { color: "var(--amber-400)", label: "Verification"      },
};

export default function NotificationCenter() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== "all") params.set("type", filterType);
      if (unreadOnly) params.set("unread_only", "true");
      const { data } = await api.get(`/notifications?${params}`);
      setItems(data);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType, unreadOnly]);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(n => (n.message || "").toLowerCase().includes(q) || (n.kind || "").toLowerCase().includes(q));
  }, [items, search]);

  const knownTypes = Array.from(new Set(items.map(i => i.kind)));

  const route = (n) => {
    const meta = n.meta || {};
    if ((n.kind || "").startsWith("rfq_") && meta.rfq_id) return `/app/rfqs/${meta.rfq_id}`;
    if ((n.kind || "").startsWith("ticket_") && meta.ticket_id) return `/app/tickets/${meta.ticket_id}`;
    if ((n.kind || "").startsWith("quote_") && meta.rfq_id) return `/app/rfqs/${meta.rfq_id}`;
    if (n.kind === "rfq_awarded" && meta.order_id) return `/app/orders/${meta.order_id}`;
    if ((n.kind || "").startsWith("order_") && meta.order_id) return `/app/orders/${meta.order_id}`;
    if (n.kind === "award" && meta.order_id) return `/app/orders/${meta.order_id}`;
    if (n.kind === "verification_approved" || n.kind === "verification_rejected" || n.kind === "verification") return `/app/company`;
    if (n.kind === "invite_sent") return "/app/team";
    return null;
  };

  const open = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    const url = route(n);
    if (url) nav(url);
    else load();
  };
  const toggleRead = async (e, n) => { e.stopPropagation();
    await api.post(`/notifications/${n.id}/${n.read ? "unread" : "read"}`); load();
  };
  const dismiss = async (e, n) => { e.stopPropagation();
    await api.delete(`/notifications/${n.id}`); load();
  };
  const markAll = async () => { await api.post("/notifications/read_all"); load(); };

  const unreadCount = items.filter(i => !i.read).length;

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle={`${items.length} total · ${unreadCount} unread`}
        actions={unreadCount > 0 ? <button data-testid="mark-all-read" onClick={markAll} className="btn btn-ghost" style={{ fontSize: 12 }}><Check size={12} /> Mark all as read</button> : null}
      />

      <div className="vault-toolbar">
        <div className="vault-search">
          <Search size={14} color="var(--ink-mute)" />
          <input className="input" data-testid="notif-search" placeholder="Search notifications…" value={search} onChange={e => setSearch(e.target.value)} style={{ border: 0, padding: "6px 8px", background: "transparent" }} />
        </div>
        <div className="vault-filters">
          <button onClick={() => setFilterType("all")} data-testid="notif-filter-all" className={`btn ${filterType === "all" ? "btn-primary" : "btn-ghost"}`} style={{ padding: "5px 12px", fontSize: 12 }}>All types</button>
          {knownTypes.map(t => (
            <button key={t} onClick={() => setFilterType(t)} data-testid={`notif-filter-${t}`} className={`btn ${filterType === t ? "btn-primary" : "btn-ghost"}`} style={{ padding: "5px 12px", fontSize: 12 }}>
              {TYPE_CONFIG[t]?.label || t}
            </button>
          ))}
          <button onClick={() => setUnreadOnly(v => !v)} data-testid="notif-unread-toggle" className={`btn ${unreadOnly ? "btn-primary" : "btn-ghost"}`} style={{ padding: "5px 12px", fontSize: 12 }}>Unread only</button>
        </div>
      </div>

      {busy && <div style={{ color: "var(--ink-mute)" }}>Loading…</div>}
      {!busy && filtered.length === 0 && (
        <div className="card" style={{ padding: 48, textAlign: "center", color: "var(--ink-mute)" }}>
          <Bell size={36} style={{ margin: "0 auto 12px", opacity: .4 }} />
          <div>No notifications match your filters.</div>
        </div>
      )}

      <div className="notif-list">
        {filtered.map(n => {
          const cfg = TYPE_CONFIG[n.kind] || { color: "var(--ink-dim)", label: n.kind };
          const url = route(n);
          return (
            <div key={n.id} data-testid={`notif-row-${n.id}`} onClick={() => open(n)}
                 className={`notif-row ${!n.read ? "is-unread" : ""}`}>
              <div className="notif-dot" style={{ background: cfg.color }} aria-hidden="true" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="notif-head">
                  <span className="font-mono notif-type" style={{ color: cfg.color }}>{cfg.label}</span>
                  <span className="notif-time font-mono">{timeAgo(n.created_at)}</span>
                </div>
                <div className="notif-message">{n.message}</div>
              </div>
              <div className="notif-actions">
                {url && <ExternalLink size={14} color="var(--ink-mute)" />}
                <button data-testid={`notif-toggle-${n.id}`} onClick={(e) => toggleRead(e, n)} className="btn btn-ghost" style={{ padding: 4, fontSize: 11 }} title={n.read ? "Mark as unread" : "Mark as read"}>
                  <Check size={12} color={n.read ? "var(--ink-mute)" : "var(--amber-500)"} />
                </button>
                <button data-testid={`notif-dismiss-${n.id}`} onClick={(e) => dismiss(e, n)} className="btn btn-ghost" style={{ padding: 4, fontSize: 11 }} title="Dismiss">
                  <X size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function timeAgo(iso) {
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24); if (d < 7)  return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
